import React, { useEffect, useState } from 'react'
import { Box, Chip, Stack, Typography } from '@mui/material'
import ConfirmDialog from '../../shared/dialogs/ConfirmDialog'
import { userApi, User } from '../../../services/userApi'

interface DeleteUserDialogProps {
  open: boolean
  onClose: () => void
  user: User | null
  onSuccess: (message: string) => void
  onError: (message: string) => void
}

/**
 * Permanently delete a user. Thin wrapper over the shared `ConfirmDialog`
 * (danger tone + typed-email confirmation). The confirm-text gate now lives
 * in `ConfirmDialog`; this wrapper keeps the `userApi.deleteUser` call and
 * maps loading/error onto `pending` / `errorText`. Prop contract unchanged.
 */
const DeleteUserDialog: React.FC<DeleteUserDialogProps> = ({
  open,
  onClose,
  user,
  onSuccess,
  onError,
}) => {
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) {
      setError(null)
      setLoading(false)
    }
  }, [open])

  const handleDelete = async () => {
    if (!user) return
    try {
      setLoading(true)
      setError(null)
      // ConfirmDialog already verified the typed email matches; send the
      // canonical address so the backend match never hinges on casing.
      await userApi.deleteUser(user.id, user.email)
      onSuccess(`User ${user.email} has been permanently deleted`)
      onClose()
    } catch (err: any) {
      const message = err.response?.data?.detail || err.message || 'Failed to delete user'
      setError(message)
      onError(message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={handleDelete}
      tone="danger"
      title="Permanently Delete User"
      pending={loading}
      confirmLabel="Delete Permanently"
      confirmText={user?.email}
      confirmTextLabel="Type the user's email to confirm"
      errorText={error}
      body={<DeleteUserBody user={user} />}
    />
  )
}

const DeleteUserBody: React.FC<{ user: User | null }> = ({ user }) => (
  <Box>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>
      This action cannot be undone.
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
      The user and all related data (sessions, franchise access, invitations) is
      permanently removed.
    </Typography>

    {user && (
      <Stack spacing={0.75} sx={{ mt: 1.5 }}>
        <Typography variant="body2">
          <strong>Name:</strong> {user.name}
        </Typography>
        <Typography variant="body2">
          <strong>Email:</strong> {user.email}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2">
            <strong>Role:</strong>
          </Typography>
          <Chip label={user.role} size="small" />
        </Stack>
      </Stack>
    )}
  </Box>
)

export default DeleteUserDialog
